<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card">
      <!-- 筛选条件 -->
      <el-form :model="query" :inline="true" class="glass-form">
        <el-form-item :label="$t('common.consumptionDate')">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            size="small"
            style="width: 280px"
            :range-separator="$t('common.dateRangeSep')"
            :start-placeholder="$t('common.dateStart')"
            :end-placeholder="$t('common.dateEnd')"
            value-format="yyyy-MM-dd"
            :picker-options="datePickerOptions"
            @change="handleDateChange"
          />
        </el-form-item>
        <el-form-item :label="$t('userSummary.userName')">
          <el-select
            v-model="query.userName"
            clearable
            filterable
            :placeholder="$t('userSummary.userNamePlaceholder')"
            size="small"
            style="width: 200px"
          >
            <el-option
              v-for="name in creatorOptions"
              :key="name"
              :label="name"
              :value="name"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="text" @click="showAdvanced = !showAdvanced">
            {{ showAdvanced ? $t('userSummary.hideFilter') : $t('userSummary.moreFilter') }}
            <i :class="showAdvanced ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </el-button>
          <el-button type="primary" icon="el-icon-search" size="mini" @click="handleQuery">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">{{ $t('common.reset') }}</el-button>
          <el-button type="warning" plain icon="el-icon-download" size="mini" :loading="exporting" @click="handleExport">导出 Excel</el-button>
        </el-form-item>
      </el-form>
      <!-- 指标范围筛选 -->
      <el-form v-show="showAdvanced" :model="query" :inline="true" class="glass-form metric-filter-row">
        <el-form-item :label="$t('userSummary.spend')">
          <el-input-number v-model="query.spendMin" :placeholder="$t('common.min')" size="mini" :controls="false" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.spendMax" :placeholder="$t('common.max')" size="mini" :controls="false" style="width: 90px" />
        </el-form-item>
        <el-form-item :label="$t('userSummary.ctr')">
          <el-input-number v-model="query.ctrMinPct" :placeholder="$t('common.min')" size="mini" :controls="false" :precision="2" style="width: 80px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.ctrMaxPct" :placeholder="$t('common.max')" size="mini" :controls="false" :precision="2" style="width: 80px" />
          <span class="range-unit">%</span>
        </el-form-item>
        <el-form-item :label="$t('userSummary.purchases')">
          <el-input-number v-model="query.purchasesMin" :placeholder="$t('common.min')" size="mini" :controls="false" :precision="0" style="width: 80px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.purchasesMax" :placeholder="$t('common.max')" size="mini" :controls="false" :precision="0" style="width: 80px" />
        </el-form-item>
        <el-form-item :label="$t('userSummary.costPerPurchase')">
          <el-input-number v-model="query.costPerPurchaseMin" :placeholder="$t('common.min')" size="mini" :controls="false" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.costPerPurchaseMax" :placeholder="$t('common.max')" size="mini" :controls="false" style="width: 90px" />
        </el-form-item>
        <el-form-item :label="$t('userSummary.purchaseValue')">
          <el-input-number v-model="query.purchaseValueMin" :placeholder="$t('common.min')" size="mini" :controls="false" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.purchaseValueMax" :placeholder="$t('common.max')" size="mini" :controls="false" style="width: 90px" />
        </el-form-item>
        <el-form-item label="ROI">
          <el-input-number v-model="query.roiMin" :placeholder="$t('common.min')" size="mini" :controls="false" :precision="4" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.roiMax" :placeholder="$t('common.max')" size="mini" :controls="false" :precision="4" style="width: 90px" />
        </el-form-item>
      </el-form>

      <!-- 数据表格 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        border
        size="small"
        :header-cell-style="{ background: '#f8fafd', color: '#606266', fontWeight: '600' }"
        @sort-change="handleSortChange"
      >
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column :label="$t('userSummary.userName')" prop="createUserName" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.createUserName || row.objectId || '-' }}</template>
        </el-table-column>
        <el-table-column :label="$t('userSummary.spend')" width="120" align="right" sortable="custom" prop="spend">
          <template slot-scope="{ row }">{{ formatMoney(row.spend) }}</template>
        </el-table-column>
        <el-table-column :label="$t('userSummary.ctr')" width="100" align="right" sortable="custom" prop="ctr">
          <template slot-scope="{ row }">{{ formatPercent(row.ctr) }}</template>
        </el-table-column>
        <el-table-column :label="$t('userSummary.purchases')" width="90" align="right" sortable="custom" prop="purchases">
          <template slot-scope="{ row }">{{ formatInt(row.purchases) }}</template>
        </el-table-column>
        <el-table-column :label="$t('userSummary.costPerPurchase')" width="140" align="right" sortable="custom" prop="costPerPurchase">
          <template slot-scope="{ row }">{{ formatMoney(row.costPerPurchase) }}</template>
        </el-table-column>
        <el-table-column :label="$t('userSummary.purchaseValue')" width="120" align="right" sortable="custom" prop="purchaseValue">
          <template slot-scope="{ row }">{{ formatMoney(row.purchaseValue) }}</template>
        </el-table-column>
        <el-table-column label="ROI" width="100" align="right" sortable="custom" prop="roi">
          <template slot-scope="{ row }">{{ formatDecimal(row.roi) }}</template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="120">
          <template slot-scope="{ row }">
            <el-button size="mini" type="text" icon="el-icon-view" @click="handleViewDaily(row)">{{ $t('userSummary.viewDaily') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <pagination
        v-show="total > 0"
        :total="total"
        :page.sync="pageNo"
        :limit.sync="pageSize"
        @pagination="fetchData"
      />

      <!-- 汇总行 -->
      <div class="summary-row" v-if="tableData.length > 0">
        <span class="summary-label">{{ $t('userSummary.summaryLabel', { total: total }) }}</span>
        <span>{{ $t('userSummary.spend') }} <b>{{ formatMoney(summary.spend) }}</b></span>
        <span>{{ $t('userSummary.purchases') }} <b>{{ formatInt(summary.purchases) }}</b></span>
        <span>{{ $t('userSummary.purchaseValue') }} <b>{{ formatMoney(summary.purchaseValue) }}</b></span>
      </div>
    </el-card>

    <!-- 每日明细弹窗 -->
    <el-dialog
      :title="dailyDialogTitle"
      :visible.sync="dailyVisible"
      width="900px"
      top="5vh"
      append-to-body
      destroy-on-close
    >
      <div v-loading="dailyLoading">
        <el-table
          v-if="dailyList.length > 0"
          :data="dailyList"
          border
          size="small"
          :header-cell-style="{ background: '#f8fafd', color: '#606266', fontWeight: '600' }"
        >
          <el-table-column :label="$t('userSummary.date')" prop="date" width="120" align="center" />
          <el-table-column :label="$t('userSummary.spend')" width="110" align="right">
            <template slot-scope="{ row }">{{ formatMoney(row.spend) }}</template>
          </el-table-column>
          <el-table-column :label="$t('userSummary.ctr')" width="100" align="right">
            <template slot-scope="{ row }">{{ formatPercent(row.ctr) }}</template>
          </el-table-column>
          <el-table-column :label="$t('userSummary.purchases')" width="90" align="right">
            <template slot-scope="{ row }">{{ formatInt(row.purchases) }}</template>
          </el-table-column>
          <el-table-column :label="$t('userSummary.costPerPurchase')" width="140" align="right">
            <template slot-scope="{ row }">{{ formatMoney(row.costPerPurchase) }}</template>
          </el-table-column>
          <el-table-column :label="$t('userSummary.purchaseValue')" width="120" align="right">
            <template slot-scope="{ row }">{{ formatMoney(row.purchaseValue) }}</template>
          </el-table-column>
          <el-table-column label="ROI" width="100" align="right">
            <template slot-scope="{ row }">{{ formatDecimal(row.roi) }}</template>
          </el-table-column>
        </el-table>
        <el-empty v-else :description="$t('userSummary.noData')" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { queryInsightsAggPage, queryInsightsDaily, exportInsightsAgg, queryCreatorOptions } from '@/api/report/insights';
import { confirmExportLargeRows } from '@/utils/export';
import { withLocalDevLongRangeDateShortcuts } from '@/utils/dateRangePickerOptions';
import Pagination from '@/components/Pagination';

export default {
  name: 'UserSummary',
  components: { Pagination },
  data() {
    return {
      loading: false,
      dateRange: [],
      datePickerOptions: withLocalDevLongRangeDateShortcuts([
        { text: this.$t('userSummary.last7days'), onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 6); picker.$emit('pick', [s, e]); } },
        { text: this.$t('userSummary.last14days'), onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 13); picker.$emit('pick', [s, e]); } },
        { text: this.$t('userSummary.last30days'), onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 29); picker.$emit('pick', [s, e]); } }
      ]),
      showAdvanced: false,
      query: {
        userName: undefined,
        startDate: undefined,
        endDate: undefined,
        spendMin: undefined,
        spendMax: undefined,
        ctrMinPct: undefined,
        ctrMaxPct: undefined,
        purchasesMin: undefined,
        purchasesMax: undefined,
        costPerPurchaseMin: undefined,
        costPerPurchaseMax: undefined,
        purchaseValueMin: undefined,
        purchaseValueMax: undefined,
        roiMin: undefined,
        roiMax: undefined
      },
      pageNo: 1,
      pageSize: 10,
      total: 0,
      tableData: [],
      globalSummary: null,
      sortField: 'spend',
      sortOrder: 'desc',
      // 每日明细弹窗
      dailyVisible: false,
      dailyLoading: false,
      dailyDialogTitle: '',
      dailyList: [],
      exporting: false,
      creatorOptions: []
    };
  },
  computed: {
    summary() {
      const s = this.globalSummary;
      if (s) {
        return {
          spend: Number(s.spend) || 0,
          purchases: Number(s.purchases) || 0,
          purchaseValue: Number(s.purchase_value) || 0
        };
      }
      let spend = 0, purchases = 0, purchaseValue = 0;
      for (const row of this.tableData) {
        spend += Number(row.spend) || 0;
        purchases += Number(row.purchases) || 0;
        purchaseValue += Number(row.purchaseValue) || 0;
      }
      return { spend, purchases, purchaseValue };
    }
  },
  created() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    const fmt = function(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
    this.dateRange = [fmt(start), fmt(end)];
    this.query.startDate = fmt(start);
    this.query.endDate = fmt(end);
    this.loadCreatorOptions();
    this.fetchData();
  },
  methods: {
    async loadCreatorOptions() {
      try {
        const res = await queryCreatorOptions();
        this.creatorOptions = res.data || [];
      } catch (e) {
        this.creatorOptions = [];
      }
    },
    async fetchData() {
      this.loading = true;
      try {
        const q = this.query;
        const params = {
          dimension: 'user',
          pageNo: this.pageNo,
          pageSize: this.pageSize,
          startDate: q.startDate,
          endDate: q.endDate,
          userName: q.userName,
          sortField: this.sortField,
          sortOrder: this.sortOrder,
          spendMin: q.spendMin,
          spendMax: q.spendMax,
          ctrMin: q.ctrMinPct != null ? q.ctrMinPct / 100 : undefined,
          ctrMax: q.ctrMaxPct != null ? q.ctrMaxPct / 100 : undefined,
          purchasesMin: q.purchasesMin,
          purchasesMax: q.purchasesMax,
          costPerPurchaseMin: q.costPerPurchaseMin,
          costPerPurchaseMax: q.costPerPurchaseMax,
          purchaseValueMin: q.purchaseValueMin,
          purchaseValueMax: q.purchaseValueMax,
          roiMin: q.roiMin,
          roiMax: q.roiMax
        };
        const res = await queryInsightsAggPage(params);
        const pageData = res.data || {};
        this.tableData = pageData.records || [];
        this.total = pageData.total || 0;
        this.globalSummary = pageData.summary || null;
      } finally {
        this.loading = false;
      }
    },
    handleQuery() {
      this.pageNo = 1;
      this.fetchData();
    },
    resetQuery() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 6);
      const fmt = function(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
      this.dateRange = [fmt(start), fmt(end)];
      this.query = {
        userName: undefined,
        startDate: fmt(start),
        endDate: fmt(end),
        spendMin: undefined,
        spendMax: undefined,
        ctrMinPct: undefined,
        ctrMaxPct: undefined,
        purchasesMin: undefined,
        purchasesMax: undefined,
        costPerPurchaseMin: undefined,
        costPerPurchaseMax: undefined,
        purchaseValueMin: undefined,
        purchaseValueMax: undefined,
        roiMin: undefined,
        roiMax: undefined
      };
      this.pageNo = 1;
      this.sortField = 'spend';
      this.sortOrder = 'desc';
      this.fetchData();
    },
    handleDateChange(val) {
      if (val && val.length === 2) {
        this.query.startDate = val[0];
        this.query.endDate = val[1];
      } else {
        this.query.startDate = undefined;
        this.query.endDate = undefined;
      }
    },
    handleSortChange({ prop, order }) {
      this.sortField = prop || 'spend';
      this.sortOrder = order === 'ascending' ? 'asc' : 'desc';
      this.pageNo = 1;
      this.fetchData();
    },
    async handleExport() {
      this.$confirm('确认导出当前筛选条件下的数据？', '导出提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const q = this.query;
        const countParams = {
          dimension: 'user',
          pageNo: 1,
          pageSize: 1,
          startDate: q.startDate,
          endDate: q.endDate,
          userName: q.userName,
          sortField: this.sortField,
          sortOrder: this.sortOrder,
          spendMin: q.spendMin,
          spendMax: q.spendMax,
          ctrMin: q.ctrMinPct != null ? q.ctrMinPct / 100 : undefined,
          ctrMax: q.ctrMaxPct != null ? q.ctrMaxPct / 100 : undefined,
          purchasesMin: q.purchasesMin,
          purchasesMax: q.purchasesMax,
          costPerPurchaseMin: q.costPerPurchaseMin,
          costPerPurchaseMax: q.costPerPurchaseMax,
          purchaseValueMin: q.purchaseValueMin,
          purchaseValueMax: q.purchaseValueMax,
          roiMin: q.roiMin,
          roiMax: q.roiMax
        };
        let countRes;
        try {
          countRes = await queryInsightsAggPage(countParams);
        } catch (e) {
          this.$message.error(this.$t('common.exportCountFail'));
          return;
        }
        try {
          await confirmExportLargeRows(this, (countRes.data || {}).total || 0);
        } catch (e) {
          return;
        }
        this.exporting = true;
        try {
          const params = {
            dimension: 'user',
            startDate: q.startDate,
            endDate: q.endDate,
            userName: q.userName,
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            spendMin: q.spendMin,
            spendMax: q.spendMax,
            ctrMin: q.ctrMinPct != null ? q.ctrMinPct / 100 : undefined,
            ctrMax: q.ctrMaxPct != null ? q.ctrMaxPct / 100 : undefined,
            purchasesMin: q.purchasesMin,
            purchasesMax: q.purchasesMax,
            costPerPurchaseMin: q.costPerPurchaseMin,
            costPerPurchaseMax: q.costPerPurchaseMax,
            purchaseValueMin: q.purchaseValueMin,
            purchaseValueMax: q.purchaseValueMax,
            roiMin: q.roiMin,
            roiMax: q.roiMax
          };
          const res = await exportInsightsAgg(params);
          const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = '用户汇总报表.xlsx';
          link.click();
          window.URL.revokeObjectURL(url);
          this.$message.success('导出成功');
        } catch (e) {
          this.$message.error('导出失败');
        } finally {
          this.exporting = false;
        }
      }).catch(function() {});
    },
    async handleViewDaily(row) {
      const userName = row.createUserName || row.objectId;
      if (!userName) {
        this.$message.warning(this.$t('userSummary.noUserName'));
        return;
      }
      const dateInfo = this.query.startDate && this.query.endDate
        ? ' (' + this.query.startDate + ' ~ ' + this.query.endDate + ')'
        : '';
      this.dailyDialogTitle = this.$t('userSummary.dailyDialogTitle') + userName + dateInfo;
      this.dailyVisible = true;
      this.dailyLoading = true;
      this.dailyList = [];
      try {
        const res = await queryInsightsDaily({
          dimension: 'user',
          objectId: userName,
          startDate: this.query.startDate,
          endDate: this.query.endDate
        });
        this.dailyList = res.data || [];
      } catch (e) {
        this.$message.error(this.$t('userSummary.loadDailyFailed') + (e.message || ''));
      } finally {
        this.dailyLoading = false;
      }
    },
    formatMoney(val) {
      if (val == null) return '-';
      return Number(val).toFixed(2);
    },
    formatDecimal(val) {
      if (val == null) return '-';
      return Number(val).toFixed(4);
    },
    formatPercent(val) {
      if (val == null) return '-';
      return (Number(val) * 100).toFixed(2) + '%';
    },
    formatInt(val) {
      if (val == null) return '-';
      return Number(val).toLocaleString();
    }
  }
};
</script>

<style scoped>
.summary-row {
  margin-top: 12px;
  padding: 10px 16px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
}
.summary-label {
  font-weight: 600;
  color: #303133;
}
.summary-row b {
  color: #409EFF;
}
.metric-filter-row {
  border-top: 1px dashed #DCDFE6;
  padding-top: 10px;
  margin-top: 4px;
}
.range-sep {
  margin: 0 4px;
  color: #909399;
}
.range-unit {
  margin-left: 2px;
  font-size: 12px;
  color: #909399;
}
</style>
